import {
	datePeriodToRange,
	parseDateRoute,
	rangeToDatePeriod,
	timestampToMonth,
} from './algolia-filter-date';

const normalizeRouteValues = (value) => {
	if (Array.isArray(value)) {
		return value.filter((item) => typeof item === 'string' && item);
	}

	return typeof value === 'string' && value ? [value] : [];
};

export const getRouting = ({
	config,
	facets,
	dateDescriptor,
	historyRouter,
	onDateError,
}) => {
	const searchEnabled = facets.some((facet) => facet.type === 'search');
	const refinements = facets.filter(
		(facet) => facet.type === 'refinement_list' && facet.id && facet.attribute
	);
	const managedKeys = [
		...(searchEnabled ? ['search'] : []),
		...refinements.map((facet) => facet.id),
		...(dateDescriptor ? ['date', 'date_start', 'date_end'] : []),
		'pg',
	];

	return {
		router: historyRouter({
			createURL({ qsModule, routeState, location }) {
				const existing = qsModule.parse(location.search.slice(1));
				managedKeys.forEach((key) => delete existing[key]);
				const query = qsModule.stringify(
					{ ...existing, ...routeState },
					{ addQueryPrefix: true, arrayFormat: 'repeat' }
				);
				return `${location.origin}${location.pathname}${query}${location.hash}`;
			},
			parseURL({ qsModule, location }) {
				const query = qsModule.parse(location.search.slice(1));
				return managedKeys.reduce((routeState, key) => {
					if (query[key] !== undefined) {
						routeState[key] = query[key];
					}
					return routeState;
				}, {});
			},
		}),
		stateMapping: {
			stateToRoute(uiState) {
				const indexState = uiState[config.primary_index_name] || {};
				const routeState = {};

				if (searchEnabled && indexState.query) {
					routeState.search = indexState.query;
				}
				refinements.forEach((facet) => {
					const values = indexState.refinementList?.[facet.attribute];
					if (Array.isArray(values) && values.length) {
						routeState[facet.id] = values;
					}
				});
				if (dateDescriptor) {
					const range = indexState.range?.[dateDescriptor.attribute];
					if (typeof range === 'string') {
						const [start, end] = range.split(':').map(
							(value) => value === '' ? undefined : Number(value)
						);
						const period = rangeToDatePeriod(
							[start, end],
							config.site_timezone
						);
						if (period && period !== 'any-time') {
							routeState.date = period;
						} else {
							const startMonth = timestampToMonth(start, config.site_timezone);
							const endMonth = timestampToMonth(end, config.site_timezone);
							if (startMonth) {
								routeState.date_start = startMonth;
							}
							if (endMonth) {
								routeState.date_end = endMonth;
							}
						}
					}
				}
				if (Number(indexState.page) > 1) {
					routeState.pg = Number(indexState.page);
				}

				return routeState;
			},
			routeToState(routeState) {
				const indexState = {};
				const query = Array.isArray(routeState.search) ? routeState.search[0] : routeState.search;
				if (searchEnabled && typeof query === 'string' && query) {
					indexState.query = query;
				}

				const refinementList = {};
				refinements.forEach((facet) => {
					const values = normalizeRouteValues(routeState[facet.id]);
					if (values.length) {
						refinementList[facet.attribute] = values;
					}
				});
				if (Object.keys(refinementList).length) {
					indexState.refinementList = refinementList;
				}

				if (dateDescriptor) {
					const period = Array.isArray(routeState.date)
						? routeState.date[0]
						: routeState.date;
					const periodRange = typeof period === 'string'
						? datePeriodToRange(period, config.site_timezone)
						: null;
					if (periodRange && periodRange[0] !== undefined) {
						indexState.range = {
							[dateDescriptor.attribute]: `${periodRange[0]}:`,
						};
					}

					const startMonth = typeof routeState.date_start === 'string'
						? routeState.date_start
						: '';
					const endMonth = typeof routeState.date_end === 'string'
						? routeState.date_end
						: '';
					const parsed = parseDateRoute(
						startMonth,
						endMonth,
						config.site_timezone
					);
					onDateError(parsed.error);
					if (
						!indexState.range
						&& !parsed.error
						&& (parsed.start !== undefined || parsed.end !== undefined)
					) {
						indexState.range = {
							[dateDescriptor.attribute]: `${parsed.start ?? ''}:${parsed.end ?? ''}`,
						};
					}
				}

				const page = Number(routeState.pg);
				if (Number.isInteger(page) && page > 1) {
					indexState.page = page;
				}

				return {
					[config.primary_index_name]: indexState,
				};
			},
		},
	};
};
